import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Input,
  Textarea,
  Select,
  Card,
  Badge,
  Modal,
  FileUpload,
} from "../components/ui";
import AnteprimaDocumenti from "../components/AnteprimaDocumenti";

// Ruoli admin
const RUOLI_ADMIN = [100000000, 100000001, 100000002]; // Admin, RSPP, Direzione

function DettaglioInterventoPage() {
  const { id } = useParams();
  const { user, apiRequest, canPerformAction } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [intervento, setIntervento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteValidazione, setNoteValidazione] = useState("");
  const [errors, setErrors] = useState({});
  const [documenti, setDocumenti] = useState([]);
  const [azioniCorrettive, setAzioniCorrettive] = useState([]);
  const [tipologieList, setTipologieList] = useState([]); // tutte le tipologie disponibili
  const [tipologieCollegate, setTipologieCollegate] = useState([]); // tipologie N:N dell'intervento
  const [tipologiaDaAggiungere, setTipologiaDaAggiungere] = useState(""); // selezione nel dropdown add
  const [scadenzeExtraCollegate, setScadenzeExtraCollegate] = useState([]); // scadenze aggiuntive N:N dell'intervento
  const [scadenzeExtraDisponibili, setScadenzeExtraDisponibili] = useState([]); // scadenze aperte selezionabili
  const [scadenzaExtraDaAggiungere, setScadenzaExtraDaAggiungere] =
    useState(""); // selezione nel dropdown add

  // Form data per editing
  const [formData, setFormData] = useState({});

  // Permessi
  const isAdmin = RUOLI_ADMIN.includes(user?.ruolo);
  const canValidate = canPerformAction("interventi.valida");
  const isFornitore = user?.ruolo === 100000005;
  const isOwner =
    isFornitore && intervento?.fornitore?.id === user?.fornitoreId;
  const canEdit =
    isAdmin ||
    (isOwner &&
      !intervento?.validato &&
      intervento?.statoValidazione !== 100000002);
  const canDelete = canPerformAction("interventi.elimina");

  // Esito opzioni
  const esitoOptions = [
    { value: 100000000, label: "Positivo", color: "green" },
    { value: 100000001, label: "Negativo", color: "red" },
  ];

  useEffect(() => {
    const fetchIntervento = async () => {
      try {
        const [
          data,
          docsResult,
          acResult,
          tipologieResult,
          tipCollegateResult,
          scadenzeExtraResult,
        ] = await Promise.all([
          apiRequest(`/interventi/${id}`),
          apiRequest(`/documenti?registroId=${id}&tipoDocumento=100000003`),
          apiRequest(`/azioni-correttive?interventoId=${id}`).catch(() => ({
            value: [],
          })),
          apiRequest("/tipologie-intervento").catch(() => ({ value: [] })),
          apiRequest(`/interventi/${id}/tipologie`).catch(() => ({
            value: [],
          })),
          apiRequest(`/interventi/${id}/scadenze-aggiuntive`).catch(() => ({
            value: [],
          })),
        ]);
        setIntervento(data);
        setDocumenti(docsResult.value || []);
        setAzioniCorrettive(acResult.value || []);
        setTipologieList(tipologieResult.value || []);
        setTipologieCollegate(tipCollegateResult.value || []);
        setScadenzeExtraCollegate(scadenzeExtraResult.value || []);
        setFormData({
          nome: data.nome || "",
          dataIntervento: data.dataIntervento?.split("T")[0] || "",
          esito: data.esito || 100000000,
          tipologiaInterventoId: data.tipologiaInterventoId || "",
          nomeTecnico: data.nomeTecnico || "",
          oreLavorate: data.oreLavorate || "",
          costoIntervento: data.costoIntervento || "",
          descrizione: data.descrizione || "",
          materialiUtilizzati: data.materialiUtilizzati || "",
        });
      } catch (error) {
        console.error("Error fetching intervento:", error);
        showToast("Errore nel caricamento dell'intervento", "error");
        navigate("/interventi");
      } finally {
        setLoading(false);
      }
    };

    fetchIntervento();
  }, [id, apiRequest, showToast, navigate]);

  // Carica le scadenze aperte selezionabili come "aggiuntive" (stessa RSA + fornitore)
  useEffect(() => {
    if (!intervento?.rsa?.id || !intervento?.fornitore?.id) {
      setScadenzeExtraDisponibili([]);
      return;
    }

    const fetchScadenzeDisponibili = async () => {
      try {
        const params = new URLSearchParams({
          rsaId: intervento.rsa.id,
          fornitoreId: intervento.fornitore.id,
        });
        const result = await apiRequest(
          `/interventi/check-scadenze-aperte?${params.toString()}`,
        );
        setScadenzeExtraDisponibili(result.scadenzeAperte || []);
      } catch (error) {
        console.error("Error loading scadenze disponibili:", error);
        setScadenzeExtraDisponibili([]);
      }
    };

    fetchScadenzeDisponibili();
  }, [intervento?.rsa?.id, intervento?.fornitore?.id, apiRequest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.nome?.trim()) newErrors.nome = "Il nome e obbligatorio";
    if (!formData.dataIntervento)
      newErrors.dataIntervento = "La data e obbligatoria";
    if (!formData.descrizione?.trim())
      newErrors.descrizione = "La descrizione e obbligatoria";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      showToast("Correggi gli errori nel form", "warning");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: formData.nome,
        dataIntervento: formData.dataIntervento,
        esito: parseInt(formData.esito),
        descrizione: formData.descrizione,
      };

      if (formData.tipologiaInterventoId)
        payload.tipologiaInterventoId = formData.tipologiaInterventoId;
      if (formData.nomeTecnico) payload.nomeTecnico = formData.nomeTecnico;
      if (formData.oreLavorate)
        payload.oreLavorate = parseFloat(formData.oreLavorate);
      if (formData.costoIntervento)
        payload.costoIntervento = parseFloat(formData.costoIntervento);
      if (formData.materialiUtilizzati)
        payload.materialiUtilizzati = formData.materialiUtilizzati;

      await apiRequest(`/interventi/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      // Aggiorna stato locale
      setIntervento((prev) => ({ ...prev, ...payload }));
      setIsEditing(false);
      showToast("Intervento aggiornato con successo", "success");
    } catch (error) {
      console.error("Error updating intervento:", error);
      showToast(error.message || "Errore nell'aggiornamento", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddTipologia = async () => {
    if (!tipologiaDaAggiungere) return;
    try {
      await apiRequest(`/interventi/${id}/tipologie`, {
        method: "POST",
        body: JSON.stringify({ tipologiaIds: [tipologiaDaAggiungere] }),
      });
      const tip = tipologieList.find((t) => t.id === tipologiaDaAggiungere);
      if (tip) {
        setTipologieCollegate((prev) => [
          ...prev,
          {
            id: tip.id,
            nome: tip.nome,
            frequenzaValore: tip.frequenzaValore,
            frequenzaUnita: tip.frequenzaUnita,
          },
        ]);
      }
      setTipologiaDaAggiungere("");
      showToast("Tipologia aggiunta", "success");
    } catch (error) {
      showToast(
        error.message || "Errore nell'aggiunta della tipologia",
        "error",
      );
    }
  };

  const handleRemoveTipologia = async (tipologiaId) => {
    try {
      await apiRequest(`/interventi/${id}/tipologie/${tipologiaId}`, {
        method: "DELETE",
      });
      setTipologieCollegate((prev) => prev.filter((t) => t.id !== tipologiaId));
      showToast("Tipologia rimossa", "success");
    } catch (error) {
      showToast(
        error.message || "Errore nella rimozione della tipologia",
        "error",
      );
    }
  };

  const handleAddScadenzaExtra = async () => {
    if (!scadenzaExtraDaAggiungere) return;
    try {
      await apiRequest(`/interventi/${id}/scadenze-aggiuntive`, {
        method: "POST",
        body: JSON.stringify({ scadenzeIds: [scadenzaExtraDaAggiungere] }),
      });
      const scad = scadenzeExtraDisponibili.find(
        (s) => s.id === scadenzaExtraDaAggiungere,
      );
      if (scad) {
        setScadenzeExtraCollegate((prev) => [
          ...prev,
          {
            id: scad.id,
            nome: scad.nome,
            dataScadenza: scad.dataScadenza,
            stato: scad.stato,
            tipologiaIntervento: scad.tipologiaIntervento,
          },
        ]);
      }
      setScadenzaExtraDaAggiungere("");
      showToast("Scadenza aggiuntiva aggiunta", "success");
    } catch (error) {
      showToast(
        error.message || "Errore nell'aggiunta della scadenza",
        "error",
      );
    }
  };

  const handleRemoveScadenzaExtra = async (scadenzaId) => {
    try {
      await apiRequest(`/interventi/${id}/scadenze-aggiuntive/${scadenzaId}`, {
        method: "DELETE",
      });
      setScadenzeExtraCollegate((prev) =>
        prev.filter((s) => s.id !== scadenzaId),
      );
      showToast("Scadenza aggiuntiva rimossa", "success");
    } catch (error) {
      showToast(
        error.message || "Errore nella rimozione della scadenza",
        "error",
      );
    }
  };

  const handleValidate = async (action) => {
    // action: "approve" | "reject"
    setSaving(true);
    try {
      const payload = { noteInterne: noteValidazione };

      if (action === "reject") {
        payload.statoValidazione = 100000002;
        payload.validato = false;
      } else {
        payload.validato = true;
      }

      await apiRequest(`/interventi/${id}/valida`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      const newStatoValidazione = action === "approve" ? 100000001 : 100000002;
      setIntervento((prev) => ({
        ...prev,
        validato: action === "approve",
        statoValidazione: newStatoValidazione,
        noteInterne: noteValidazione,
        dataValidazione: new Date().toISOString(),
        validatoDa: user?.displayName || user?.email || "—",
      }));

      setShowValidateModal(false);
      setNoteValidazione("");

      showToast(
        action === "approve"
          ? "Intervento validato"
          : "Intervento rifiutato - Crea azione correttiva",
        "success",
      );

      // Se rifiutato, redirect alla creazione azione correttiva
      if (action === "reject") {
        navigate(
          `/azioni-correttive/nuovo?interventoId=${id}&rsaId=${intervento.rsa?.id || ""}&fornitoreId=${intervento.fornitore?.id || ""}`,
        );
      }
    } catch (error) {
      console.error("Error validating intervento:", error);
      showToast(error.message || "Errore nella validazione", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await apiRequest(`/interventi/${id}?force=true`, { method: "DELETE" });
      showToast("Intervento eliminato", "success");
      navigate("/interventi");
    } catch (error) {
      console.error("Error deleting intervento:", error);
      showToast(error.message || "Errore nell'eliminazione", "error");
    } finally {
      setSaving(false);
    }
  };

  const getEsitoInfo = (esito) => {
    return esitoOptions.find((e) => e.value === esito) || esitoOptions[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!intervento) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Intervento non trovato</p>
        <Button
          variant="ghost"
          onClick={() => navigate("/interventi")}
          className="mt-4"
        >
          Torna alla lista
        </Button>
      </div>
    );
  }

  const esitoInfo = getEsitoInfo(intervento.esito);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <div className="mb-2 text-sm text-gray-500">
        <Link to="/interventi" className="hover:text-primary-600">
          Interventi
        </Link>
        <span className="mx-2">/</span>
        <span>{intervento.nome}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {intervento.nome}
            </h1>
            {intervento.statoValidazione === 100000002 ? (
              <Badge variant="danger">Azione correttiva</Badge>
            ) : intervento.statoValidazione === 100000003 ? (
              <Badge variant="success">Validato dopo azione correttiva</Badge>
            ) : intervento.statoValidazione === 100000001 ||
              (intervento.validato &&
                intervento.statoValidazione === undefined) ? (
              <Badge variant="success">Validato</Badge>
            ) : (
              <Badge variant="warning">Da validare</Badge>
            )}
          </div>
          <p className="text-gray-500 mt-1">
            {new Date(intervento.dataIntervento).toLocaleDateString("it-IT", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {canEdit && !isEditing && (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              Modifica
            </Button>
          )}
          {canValidate &&
            (intervento.statoValidazione === 100000000 ||
              intervento.statoValidazione === 100000002 ||
              (!intervento.validato &&
                intervento.statoValidazione === undefined)) && (
              <Button
                variant="primary"
                onClick={() => setShowValidateModal(true)}
              >
                Valida
              </Button>
            )}
          {canDelete && (
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              Elimina
            </Button>
          )}
        </div>
      </div>

      {/* Alert se inserito da portale e non validato */}
      {intervento.inseritoDaPortale && !intervento.validato && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-yellow-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-yellow-800 font-medium">
              Intervento inserito dal fornitore - In attesa di validazione
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonna principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informazioni generali */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Informazioni Generali</h2>
              <Badge
                variant={
                  esitoInfo.color === "green"
                    ? "success"
                    : esitoInfo.color === "red"
                      ? "danger"
                      : esitoInfo.color === "yellow"
                        ? "warning"
                        : "default"
                }
              >
                {esitoInfo.label}
              </Badge>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <Input
                  label="Nome Intervento"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  error={errors.nome}
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Data Intervento"
                    name="dataIntervento"
                    type="date"
                    value={formData.dataIntervento}
                    onChange={handleChange}
                    error={errors.dataIntervento}
                    required
                  />
                  <Select
                    label="Esito *"
                    name="esito"
                    value={formData.esito}
                    onChange={handleChange}
                    options={esitoOptions.map((e) => ({
                      value: e.value,
                      label: e.label,
                    }))}
                    required
                  />
                </div>
                {/* Gestione tipologie N:N */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipologie Intervento
                  </label>
                  {/* Tipologie già collegate */}
                  {tipologieCollegate.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {tipologieCollegate.map((t) => (
                        <span
                          key={t.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 border border-primary-200"
                        >
                          {t.nome}
                          <button
                            type="button"
                            onClick={() => handleRemoveTipologia(t.id)}
                            className="ml-1 text-primary-500 hover:text-red-600 transition-colors"
                            title="Rimuovi"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Aggiungi nuova tipologia */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        name="tipologiaDaAggiungere"
                        value={tipologiaDaAggiungere}
                        onChange={(e) =>
                          setTipologiaDaAggiungere(e.target.value)
                        }
                        options={tipologieList
                          .filter(
                            (t) =>
                              !tipologieCollegate.some((tc) => tc.id === t.id),
                          )
                          .map((t) => ({ value: t.id, label: t.nome }))}
                        placeholder="Aggiungi tipologia..."
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddTipologia}
                      disabled={!tipologiaDaAggiungere}
                    >
                      Aggiungi
                    </Button>
                  </div>
                </div>
                {/* Gestione scadenze aggiuntive N:N */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Altre scadenze collegate
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Scadenze aperte aggiuntive chiuse insieme a questo
                    intervento (oltre alla scadenza primaria sopra indicata).
                  </p>
                  {/* Scadenze già collegate */}
                  {scadenzeExtraCollegate.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {scadenzeExtraCollegate.map((s) => (
                        <span
                          key={s.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800 border border-primary-200"
                        >
                          {s.nome || s.tipologiaIntervento}
                          <button
                            type="button"
                            onClick={() => handleRemoveScadenzaExtra(s.id)}
                            className="ml-1 text-primary-500 hover:text-red-600 transition-colors"
                            title="Rimuovi"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Aggiungi nuova scadenza */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Select
                        name="scadenzaExtraDaAggiungere"
                        value={scadenzaExtraDaAggiungere}
                        onChange={(e) =>
                          setScadenzaExtraDaAggiungere(e.target.value)
                        }
                        options={scadenzeExtraDisponibili
                          .filter(
                            (s) =>
                              s.id !== intervento.scadenzaId &&
                              !scadenzeExtraCollegate.some(
                                (sc) => sc.id === s.id,
                              ),
                          )
                          .map((s) => ({
                            value: s.id,
                            label: `${s.nome || s.tipologiaIntervento} - entro il ${new Date(s.dataScadenza).toLocaleDateString("it-IT")}`,
                          }))}
                        placeholder="Aggiungi scadenza..."
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleAddScadenzaExtra}
                      disabled={!scadenzaExtraDaAggiungere}
                    >
                      Aggiungi
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">RSA</dt>
                  <dd className="font-medium">{intervento.rsa?.nome || "-"}</dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Fornitore</dt>
                  <dd className="font-medium">
                    {intervento.fornitore?.nome || "-"}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-sm text-gray-500 mb-1">Tipologie</dt>
                  <dd>
                    {tipologieCollegate.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {tipologieCollegate.map((t) => (
                          <span
                            key={t.id}
                            className="px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-800 border border-primary-200"
                          >
                            {t.nome}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Tipo Manutenzione</dt>
                  <dd className="font-medium">
                    {intervento.tipoManutenzioneLabel || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Contratto</dt>
                  <dd className="font-medium">{intervento.contratto || "-"}</dd>
                </div>
                {intervento.scadenza && (
                  <div className="col-span-2">
                    <dt className="text-sm text-gray-500">
                      Scadenza collegata
                    </dt>
                    <dd className="font-medium">{intervento.scadenza}</dd>
                  </div>
                )}
                {scadenzeExtraCollegate.length > 0 && (
                  <div className="col-span-2">
                    <dt className="text-sm text-gray-500 mb-1">
                      Altre scadenze collegate
                    </dt>
                    <dd>
                      <div className="flex flex-wrap gap-1.5">
                        {scadenzeExtraCollegate.map((s) => (
                          <span
                            key={s.id}
                            className="px-2 py-0.5 rounded-full text-xs bg-primary-100 text-primary-800 border border-primary-200"
                          >
                            {s.nome || s.tipologiaIntervento}
                          </span>
                        ))}
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </Card>

          {/* Dettagli tecnici */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">Dettagli Tecnici</h2>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="Nome Tecnico"
                    name="nomeTecnico"
                    value={formData.nomeTecnico}
                    onChange={handleChange}
                  />
                  <Input
                    label="Ore Lavorate"
                    name="oreLavorate"
                    type="number"
                    step="0.5"
                    value={formData.oreLavorate}
                    onChange={handleChange}
                  />
                  <Input
                    label="Costo (€)"
                    name="costoIntervento"
                    type="number"
                    step="0.01"
                    value={formData.costoIntervento}
                    onChange={handleChange}
                  />
                </div>
                <Textarea
                  label="Descrizione"
                  name="descrizione"
                  value={formData.descrizione}
                  onChange={handleChange}
                  rows={4}
                  error={errors.descrizione}
                  required
                />
                <Textarea
                  label="Materiali Utilizzati"
                  name="materialiUtilizzati"
                  value={formData.materialiUtilizzati}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <dt className="text-sm text-gray-500">Tecnico</dt>
                    <dd className="font-medium">
                      {intervento.nomeTecnico || "-"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Ore lavorate</dt>
                    <dd className="font-medium">
                      {intervento.oreLavorate
                        ? `${intervento.oreLavorate}h`
                        : "-"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-gray-500">Costo</dt>
                    <dd className="font-medium">
                      {intervento.costoIntervento
                        ? `€ ${intervento.costoIntervento.toFixed(2)}`
                        : "-"}
                    </dd>
                  </div>
                </div>

                <div>
                  <dt className="text-sm text-gray-500 mb-1">Descrizione</dt>
                  <dd className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                    {intervento.descrizione || "Nessuna descrizione"}
                  </dd>
                </div>

                {intervento.materialiUtilizzati && (
                  <div>
                    <dt className="text-sm text-gray-500 mb-1">
                      Materiali utilizzati
                    </dt>
                    <dd className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                      {intervento.materialiUtilizzati}
                    </dd>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Azioni editing */}
          {isEditing && (
            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    nome: intervento.nome || "",
                    dataIntervento:
                      intervento.dataIntervento?.split("T")[0] || "",
                    esito: intervento.esito || 100000000,
                    tipologiaInterventoId:
                      intervento.tipologiaInterventoId || "",
                    nomeTecnico: intervento.nomeTecnico || "",
                    oreLavorate: intervento.oreLavorate || "",
                    costoIntervento: intervento.costoIntervento || "",
                    descrizione: intervento.descrizione || "",
                    materialiUtilizzati: intervento.materialiUtilizzati || "",
                  });
                  setErrors({});
                }}
                disabled={saving}
              >
                Annulla
              </Button>
              <Button onClick={handleSave} loading={saving}>
                Salva modifiche
              </Button>
            </div>
          )}

          <AnteprimaDocumenti documenti={documenti} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Stato validazione */}
          <Card>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Stato Validazione
            </h3>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {intervento.statoValidazione === 100000002 ? (
                  <>
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-red-700 font-medium">
                      Azione correttiva necessaria
                    </span>
                  </>
                ) : intervento.statoValidazione === 100000003 ? (
                  <>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-green-700 font-medium">
                      Validato dopo azione correttiva
                    </span>
                  </>
                ) : intervento.statoValidazione === 100000001 ||
                  (intervento.validato &&
                    intervento.statoValidazione === undefined) ? (
                  <>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-green-700 font-medium">Validato</span>
                  </>
                ) : (
                  <>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <span className="text-yellow-700 font-medium">
                      In attesa di validazione
                    </span>
                  </>
                )}
              </div>

              {intervento.dataValidazione && (
                <div className="text-sm text-gray-500 space-y-1">
                  <p>
                    {intervento.statoValidazione === 100000002
                      ? "Segnalato"
                      : "Validato"}{" "}
                    il{" "}
                    {new Date(intervento.dataValidazione).toLocaleDateString(
                      "it-IT",
                    )}
                  </p>
                  {intervento.validatoDa && (
                    <p>
                      da{" "}
                      <span className="font-medium text-gray-700">
                        {intervento.validatoDa}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {intervento.noteInterne && (
                <div className="mt-3 p-3 bg-gray-50 rounded text-sm">
                  <p className="text-gray-500 text-xs mb-1">Note interne:</p>
                  <p>{intervento.noteInterne}</p>
                </div>
              )}

              {/* Azioni correttive collegate */}
              {azioniCorrettive.length > 0 && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    Azioni Correttive ({azioniCorrettive.length})
                  </p>
                  <div className="space-y-2">
                    {azioniCorrettive.map((ac) => (
                      <Link
                        key={ac.id}
                        to={`/azioni-correttive/${ac.id}`}
                        className="block p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium truncate">
                            {ac.soggettoAssegnato}
                          </span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              ac.stato === 100000004
                                ? "bg-gray-100 text-gray-600"
                                : ac.stato === 100000003
                                  ? "bg-blue-100 text-blue-700"
                                  : ac.stato === 100000002
                                    ? "bg-green-100 text-green-700"
                                    : ac.stato === 100000001
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-red-100 text-red-700"
                            }`}
                          >
                            {ac.statoLabel}
                          </span>
                        </div>
                        {ac.esitoValutazioneLabel && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-xs text-gray-400">
                              Esito:
                            </span>
                            <span
                              className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                ac.esitoValutazione === 100000000
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {ac.esitoValutazioneLabel}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-gray-500">
                          Scadenza:{" "}
                          {ac.scadenza
                            ? new Date(ac.scadenza).toLocaleDateString("it-IT")
                            : "-"}
                          {ac.scaduta && (
                            <span className="text-red-600 ml-1 font-medium">
                              SCADUTA
                            </span>
                          )}
                        </p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Documentazione */}
          <Card>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Documentazione
              {documenti.length > 0 && (
                <span className="ml-2 text-xs bg-gray-200 text-gray-600 rounded-full px-2 py-0.5">
                  {documenti.length}
                </span>
              )}
            </h3>

            {documenti.length > 0 ? (
              <div className="space-y-2">
                {documenti.map((doc) =>
                  doc.allegati?.length > 1 ? (
                    <div key={doc.id} className="bg-gray-50 rounded-lg p-3">
                      <p className="font-medium text-sm mb-2">
                        {doc.nome}{" "}
                        <span className="text-xs text-gray-500">
                          ({doc.allegati.length} file)
                        </span>
                      </p>
                      <div className="space-y-1">
                        {doc.allegati.map((a) => (
                          <a
                            key={a.id || a.webUrl}
                            href={a.webUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 bg-white rounded-md hover:bg-gray-100 transition-colors"
                          >
                            <svg
                              className="w-6 h-6 text-red-500 flex-shrink-0"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                              />
                            </svg>
                            <p className="min-w-0 text-sm truncate">{a.name}</p>
                          </a>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <a
                      key={doc.id}
                      href={doc.urlSharepoint}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <svg
                        className="w-8 h-8 text-red-500 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">
                          {doc.nome}
                        </p>
                        <p className="text-xs text-gray-500">
                          Clicca per aprire
                        </p>
                      </div>
                    </a>
                  ),
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Nessun documento allegato
              </p>
            )}

            {canEdit && (
              <div className="mt-3">
                <FileUpload
                  accept=".pdf,.jpg,.jpeg,.png,.zip,.rar"
                  maxSize={100}
                  multiple
                  onChange={async (files) => {
                    const fileList = Array.isArray(files)
                      ? files
                      : files
                        ? [files]
                        : [];
                    if (fileList.length === 0) return;

                    let uploadSuccessCount = 0;
                    let uploadErrorCount = 0;

                    for (let i = 0; i < fileList.length; i++) {
                      const file = fileList[i];
                      try {
                        const formDataUpload = new FormData();
                        formDataUpload.append("file", file);
                        formDataUpload.append(
                          "nome",
                          `Rapportino ${documenti.length + i + 1} - ${intervento.nome}`,
                        );
                        formDataUpload.append("tipoDocumento", "100000003");
                        formDataUpload.append("rsaId", intervento.rsa?.id);
                        formDataUpload.append("registroId", id);
                        if (intervento.fornitore?.id) {
                          formDataUpload.append(
                            "fornitoreId",
                            intervento.fornitore.id,
                          );
                        }

                        await apiRequest("/documenti/upload", {
                          method: "POST",
                          body: formDataUpload,
                          headers: {},
                        });
                        uploadSuccessCount++;
                      } catch (error) {
                        uploadErrorCount++;
                      }
                    }

                    // Ricarica documenti
                    const docsResult = await apiRequest(
                      `/documenti?registroId=${id}&tipoDocumento=100000003`,
                    );
                    setDocumenti(docsResult.value || []);

                    if (uploadErrorCount === 0) {
                      showToast(
                        uploadSuccessCount > 1
                          ? `${uploadSuccessCount} documenti caricati`
                          : "Documento caricato",
                        "success",
                      );
                    } else if (uploadSuccessCount > 0) {
                      showToast(
                        `${uploadSuccessCount} documenti caricati, ${uploadErrorCount} errore/i`,
                        "warning",
                      );
                    } else {
                      showToast("Errore nel caricamento", "error");
                    }
                  }}
                  helper="Carica uno o più rapportini"
                />
              </div>
            )}
          </Card>

          {/* Metadati */}
          <Card>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Informazioni
            </h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-gray-500">ID</dt>
                <dd className="font-mono text-xs">
                  {intervento.id?.slice(0, 8)}...
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Inserito da portale</dt>
                <dd>{intervento.inseritoDaPortale ? "Si" : "No"}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>

      {/* Modal Validazione */}
      <Modal
        isOpen={showValidateModal}
        onClose={() => setShowValidateModal(false)}
        title="Valida Intervento"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Stai per validare l'intervento "{intervento.nome}".
          </p>

          <Textarea
            label="Note (opzionale)"
            value={noteValidazione}
            onChange={(e) => setNoteValidazione(e.target.value)}
            placeholder="Aggiungi note interne sulla validazione..."
            rows={3}
          />

          {azioniCorrettive.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              <svg
                className="w-5 h-5 text-amber-600 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-amber-800">
                Esiste già un'azione correttiva collegata a questo intervento.{" "}
                <Link
                  to={`/azioni-correttive/${azioniCorrettive[0].id}`}
                  className="font-medium underline text-amber-900 hover:text-amber-700"
                >
                  Visualizza
                </Link>
              </span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="ghost" onClick={() => setShowValidateModal(false)}>
              Annulla
            </Button>
            <Button
              variant="danger"
              onClick={() => handleValidate("reject")}
              loading={saving}
              disabled={azioniCorrettive.length > 0}
              title={
                azioniCorrettive.length > 0
                  ? "Esiste già un'azione correttiva collegata"
                  : ""
              }
            >
              Rifiuta
            </Button>
            <Button
              variant="primary"
              onClick={() => handleValidate("approve")}
              loading={saving}
            >
              Approva
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Elimina */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Elimina Intervento"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sei sicuro di voler eliminare l'intervento "{intervento.nome}"?
          </p>
          <p className="text-red-600 text-sm">
            Questa azione non puo essere annullata.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={saving}>
              Elimina
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DettaglioInterventoPage;
